/**
 * Jyotish Tarang — New Chart Page
 * Birth data entry | City autocomplete | Uses fetch with explicit token
 */
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../App';
import BuyCreditModal from '../components/BuyCreditModal';

interface PlaceSuggestion {
  place_name: string;
  display: string;
  latitude: number;
  longitude: number;
  country: string;
}

function apiFetch(url: string, options?: RequestInit) {
  const token = sessionStorage.getItem('access_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers: { ...headers, ...(options?.headers || {}) } });
}

export default function ChartNew() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { t } = useTranslation();

  const [form, setForm] = useState({
    name: '', dob: '', tob: '', place_name: '', latitude: '',
    longitude: '', timezone: 'Asia/Kolkata',
    birth_time_conf: 'approximate', ayanamsha: 'lahiri', gender: '',
  });
  const [suggestions,  setSuggestions]  = useState<PlaceSuggestion[]>([]);
  const [showSuggest,  setShowSuggest]  = useState(false);
  const [placeLoading, setPlaceLoading] = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [error,        setError]        = useState('');
  const [showBuyChart, setShowBuyChart] = useState(false);
  const placeTimer = useRef<any>(null);

  function set(field: string, value: string) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function onCityChange(val: string) {
    set('place_name', val);
    set('latitude', '');
    set('longitude', '');
    clearTimeout(placeTimer.current);
    if (val.length < 2) { setSuggestions([]); setShowSuggest(false); return; }
    setPlaceLoading(true);
    placeTimer.current = setTimeout(async () => {
      try {
        const r = await apiFetch(`/api/charts/places/search?q=${encodeURIComponent(val)}`);
        const data = await r.json();
        setSuggestions(data || []);
        setShowSuggest(true);
      } catch {} finally { setPlaceLoading(false); }
    }, 400);
  }

  function selectPlace(p: PlaceSuggestion) {
    setForm(f => ({
      ...f,
      place_name: p.display || p.place_name,
      latitude:   String(p.latitude),
      longitude:  String(p.longitude),
    }));
    setSuggestions([]); setShowSuggest(false);
  }

  async function createChart(): Promise<boolean> {
    try {
      const r = await apiFetch('/api/charts/', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          latitude:  parseFloat(form.latitude),
          longitude: parseFloat(form.longitude),
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        // Phase 5 (14 Jul 2026, one-time-payments): a 403 with credit_type
        // means the tier/quota limit was hit but a ₹99 credit purchase can
        // still unlock this one chart -- offer BuyCreditModal instead of a
        // dead-end error. See charts.py::create_chart()'s credit fallback
        // (Phase 3, v4.2.2).
        if (data.credit_type === 'chart') { setShowBuyChart(true); return false; }
        setError(data.error || t('chartNew.errors.createFailed'));
        return false;
      }
      // Phase 3 (10 Jul 2026): /chart/:id (ChartView) retired -- land on
      // Today instead, which picks up the new chart via the vault selector.
      navigate('/dashboard');
      return true;
    } catch {
      setError(t('chartNew.errors.network'));
      return false;
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError(t('chartNew.errors.nameRequired')); return; }
    if (!form.dob)          { setError(t('chartNew.errors.dobRequired')); return; }
    if (!form.tob)          { setError(t('chartNew.errors.tobRequired')); return; }
    if (!form.latitude)     { setError(t('chartNew.errors.selectCity')); return; }

    setSubmitting(true);
    await createChart();
    setSubmitting(false);
  }

  async function handleCreditPurchased() {
    setShowBuyChart(false);
    setSubmitting(true);
    await createChart();
    setSubmitting(false);
  }

  return (
    <div className="page-wrap">
      <div className="container-sm">
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ fontSize:40, marginBottom:8 }}>✨</div>
          <h2 style={{ fontFamily:'var(--font-display)', color:'var(--indigo)', marginBottom:6 }}>
            {t('chartNew.title')}
          </h2>
          <p style={{ fontSize:13, color:'var(--ink3)' }}>
            {t('chartNew.subtitle')}
          </p>
        </div>

        {error && <div className="info-box info-red" style={{ marginBottom:16 }}>{error}</div>}

        <div className="card">
          <form onSubmit={handleSubmit}>

            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:16 }}>
              <div className="form-group">
                <label className="form-label">{t('chartNew.nameLabel')}</label>
                <input className="form-input" type="text"
                       value={form.name} onChange={e => set('name', e.target.value)}
                       placeholder={t('chartNew.namePlaceholder')} required/>
              </div>
              <div className="form-group">
                <label className="form-label">{t('chartNew.genderLabel')}</label>
                <select className="form-input" value={form.gender}
                        onChange={e => set('gender', e.target.value)}>
                  <option value="">{t('chartNew.gender.preferNot')}</option>
                  <option value="male">{t('chartNew.gender.male')}</option>
                  <option value="female">{t('chartNew.gender.female')}</option>
                  <option value="other">{t('chartNew.gender.other')}</option>
                </select>
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              <div className="form-group">
                <label className="form-label">{t('chartNew.dobLabel')}</label>
                <input className="form-input" type="date"
                       value={form.dob} onChange={e => set('dob', e.target.value)}
                       max={new Date().toISOString().split('T')[0]} required/>
              </div>
              <div className="form-group">
                <label className="form-label">{t('chartNew.tobLabel')}</label>
                <input className="form-input" type="time"
                       value={form.tob} onChange={e => set('tob', e.target.value)} required/>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('chartNew.accuracyLabel')}</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                {[
                  { val:'certified',   label:t('chartNew.accuracy.certified'),   hint:t('chartNew.accuracy.certifiedHint') },
                  { val:'approximate', label:t('chartNew.accuracy.approximate'), hint:t('chartNew.accuracy.approximateHint') },
                  { val:'unknown',     label:t('chartNew.accuracy.unknown'),     hint:t('chartNew.accuracy.unknownHint') },
                ].map(({ val, label, hint }) => (
                  <label key={val} style={{
                    padding:'10px 12px', borderRadius:8, cursor:'pointer',
                    border:`1.5px solid ${form.birth_time_conf===val ? 'var(--sfb)' : 'var(--parch2)'}`,
                    background: form.birth_time_conf===val ? 'var(--sfll)' : 'var(--ivory)',
                    transition:'all 0.15s',
                  }}>
                    <input type="radio" name="btc" value={val}
                           checked={form.birth_time_conf===val}
                           onChange={() => set('birth_time_conf', val)}
                           style={{ display:'none' }}/>
                    <div style={{ fontSize:12.5, fontWeight:500, color:'var(--ink)', marginBottom:2 }}>{label}</div>
                    <div style={{ fontSize:10.5, color:'var(--ink3)' }}>{hint}</div>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group" style={{ position:'relative' }}>
              <label className="form-label">{t('chartNew.placeLabel')}</label>
              <input className="form-input" type="text"
                     value={form.place_name} onChange={e => onCityChange(e.target.value)}
                     placeholder={t('chartNew.placePlaceholder')}
                     autoComplete="off"/>
              {placeLoading && (
                <div style={{ position:'absolute', right:12, top:38, fontSize:11, color:'var(--ink3)' }}>
                  {t('chartNew.searching')}
                </div>
              )}
              {showSuggest && suggestions.length > 0 && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0,
                              background:'var(--cream)', border:'1px solid var(--parch2)',
                              borderRadius:8, boxShadow:'var(--shadow-md)',
                              zIndex:100, maxHeight:220, overflowY:'auto' }}>
                  {suggestions.map((s, i) => (
                    <div key={i} onClick={() => selectPlace(s)}
                         style={{ padding:'10px 14px', cursor:'pointer', fontSize:13,
                                  borderBottom: i < suggestions.length-1 ? '0.5px solid var(--parch2)' : 'none' }}
                         onMouseEnter={e => (e.currentTarget.style.background='var(--sfll)')}
                         onMouseLeave={e => (e.currentTarget.style.background='')}>
                      <div style={{ fontWeight:500, color:'var(--ink)' }}>{s.place_name}</div>
                      <div style={{ fontSize:11, color:'var(--ink3)' }}>{s.display}</div>
                    </div>
                  ))}
                </div>
              )}
              {form.latitude && (
                <p className="form-hint" style={{ color:'var(--teal)' }}>
                  ✓ {parseFloat(form.latitude).toFixed(4)}°N, {parseFloat(form.longitude).toFixed(4)}°E
                </p>
              )}
              {!form.latitude && form.place_name.length > 2 && (
                <p className="form-hint">{t('chartNew.selectCityHint')}</p>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">{t('chartNew.ayanamshaLabel')}</label>
              <select className="form-input" value={form.ayanamsha}
                      onChange={e => set('ayanamsha', e.target.value)}>
                <option value="lahiri">{t('chartNew.ayanamsha.lahiri')}</option>
                <option value="kp">{t('chartNew.ayanamsha.kp')}</option>
                <option value="raman">{t('chartNew.ayanamsha.raman')}</option>
              </select>
            </div>

            <div className="info-box info-saffron">
              <strong>{t('chartNew.accuracyNote.title')}</strong> {t('chartNew.accuracyNote.body')}
            </div>

            <div style={{ display:'flex', gap:12, marginTop:20 }}>
              <button className="btn btn-ghost" type="button"
                      onClick={() => navigate('/dashboard')}>{t('common.cancel')}</button>
              <button className="btn btn-primary btn-full" type="submit"
                      disabled={submitting || !form.latitude}>
                {submitting ? (
                  <span>{t('chartNew.computing')}</span>
                ) : t('chartNew.submit')}
              </button>
            </div>
          </form>
        </div>

        <p style={{ textAlign:'center', fontSize:11.5, color:'var(--ink3)', marginTop:16 }}>
          🔒 {t('chartNew.privacyNote')}{' '}
          <a href="/privacy">{t('common.privacyPolicy')}</a>
        </p>
      </div>

      <BuyCreditModal open={showBuyChart} creditType="chart"
                      onClose={() => setShowBuyChart(false)}
                      onPurchased={handleCreditPurchased}/>
    </div>
  );
}
